import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CheckCircle2, Clock, Sparkles, ArrowRight } from "lucide-react";

const SPRINTS = [
  {
    id: "S1", title: "Foundation + Business Loop Starter", status: "live",
    items: [
      "Multi-tenant Organization scaffolding",
      "JWT + Google OAuth unified on JWT cookies",
      "RBAC via require_role · email-keyed lockout",
      "AppShell + sidebar by Division",
      "Executive Dashboard (KPIs · pipeline · hours)",
      "Leads (Kanban) + Clients + Quotations",
      "Projects + Production Hours + Employees",
      "In-app Architecture & Roadmap viewer",
    ],
  },
  {
    id: "S2", title: "Client-Centric Nav + Estimation + Handover", status: "live",
    items: [
      "Elevate Clients as primary nav — Client 360 as hub",
      "Requirement Analysis (light)",
      "Estimation module (discipline × software × LOD × margin)",
      "Project Handover workflow (MD → Ops accept · audited)",
      "Client Approval on quotations (tokenized public link)",
      "Monthly hours approval → auto Invoice draft",
      "Invoice + Payment recording + aging",
      "Audit Log viewer · Notifications (in-app bell)",
    ],
  },
  {
    id: "S3", title: "Resource Planning + Workload + Revisions", status: "next",
    items: [
      "Resource Planning per project (roles × hours × sw × weeks)",
      "Team Assignment (skill-matched · % allocation)",
      "Workload Management (org-wide grid · utilization %)",
      "Over-allocation & under-utilization warnings",
      "Revision Management (first-class · unlimited rounds)",
      "Billable / non-billable revisions → invoice add-ons",
      "Task Management (Kanban MVP)",
      "QA workflow + sign-off",
    ],
  },
  {
    id: "S4", title: "Industry Project Metadata + Reports", status: "",
    items: [
      "Project metadata: Discipline · LOD · Drawing Types · Software Stack",
      "Reality Capture: Matterport · Point Cloud · Drone · Laser Scan",
      "Deliverable formats: DWG · RVT · IFC · NWD · PLN · SKP · 3DM",
      "Physical metrics + Location + Compliance codes",
      "Skill-matching Employees.skills ↔ Project.software",
      "Client Communication timeline",
      "Reports Studio v1 · P&L per project · utilization · AR aging",
      "Recruitment pipeline (light)",
    ],
  },
  {
    id: "S5", title: "Assets + Documents + Vendors + HR Depth", status: "",
    items: [
      "Document Management (object storage · versioned)",
      "Vendors + Purchase Orders + Office Expenses",
      "Asset Lifecycle Management (full)",
      "  · procurement → provisioning → utilization → maintenance",
      "  · depreciation → transfer → disposal",
      "Reality-capture kit tracking (Matterport, drones, scanners)",
      "Software-license seat tracking (Revit / ArchiCAD / AutoCAD)",
      "HR depth · Payroll (IN + generic) · Attendance",
      "Statutory compliance registers (PF, ESI, GST, TDS)",
    ],
  },
  {
    id: "S6", title: "Client Portal + Employee Portal + Scale", status: "",
    items: [
      "Client Portal (external · tokenized login)",
      "  · quotation review & approval",
      "  · project status · revisions review/approve",
      "  · Matterport & Point Cloud links · invoice + pay",
      "Employee Portal (internal self-service)",
      "  · my dashboard · hours · leaves · payslips · assets · docs",
      "Multi-currency FX · Multi-timezone · i18n (EN + HI)",
      "Multi-company / Multi-branch UI org-switcher",
    ],
  },
  {
    id: "S7", title: "AI Studio", status: "",
    items: [
      "AI Quotation Assistant (Claude Sonnet 4.5)",
      "AI Email Assistant (GPT-5.2)",
      "AI Production Analysis (Gemini 3)",
      "AI Resource Allocation suggester (skill + workload aware)",
      "AI Executive Dashboard weekly digest",
      "AI Revenue Forecast (3-month projection)",
    ],
  },
];

const WORKFLOW = [
  { s: "CRM · Lead", d: "Business" },
  { s: "Client Communication", d: "Business" },
  { s: "Requirement Analysis", d: "Business" },
  { s: "Estimation", d: "Operations", new: true },
  { s: "Quotation", d: "Business" },
  { s: "Negotiation", d: "Business" },
  { s: "Client Approval", d: "Business" },
  { s: "Project Handover", d: "Business → Operations", new: true },
  { s: "Resource Planning", d: "Operations", new: true },
  { s: "Workload / Team Assignment", d: "Operations", new: true },
  { s: "Task Management", d: "Operations" },
  { s: "Production (hours logged)", d: "Operations" },
  { s: "Quality Assurance", d: "Operations" },
  { s: "Delivery", d: "Operations" },
  { s: "Revision Rounds R1..Rn", d: "Operations", new: true },
  { s: "Project Completion", d: "Operations" },
  { s: "Monthly Production Approval", d: "Operations" },
  { s: "Client Invoice", d: "Business" },
  { s: "Payment Collection", d: "Business" },
  { s: "Business Reports", d: "Business" },
];

const PERMS = [
  ["Client 360 view",         "✓", "✓", "read", "✓", "read", "—", "—"],
  ["Requirement Analysis",    "✓", "read", "—", "—", "—", "—", "—"],
  ["Estimation ✱ new",        "read", "✓", "—", "—", "read", "—", "—"],
  ["Quotation · create/send", "✓", "read", "—", "—", "—", "—", "—"],
  ["Quotation · approve",     "✓ (internal)", "—", "—", "—", "—", "—", "Client Portal (external)"],
  ["Handover ✱ new",          "initiate", "accept", "—", "—", "—", "—", "—"],
  ["Resource Plan ✱ new",     "—", "✓", "—", "—", "read", "—", "—"],
  ["Workload ✱ new",          "✓", "✓", "read", "—", "read", "—", "own"],
  ["Team Assignment",         "—", "✓", "—", "—", "read", "—", "—"],
  ["Task Management",         "—", "✓", "—", "—", "✓", "read", "—"],
  ["Log Hours",               "—", "✓", "—", "—", "✓", "✓", "—"],
  ["Approve Monthly Hours",   "—", "✓", "—", "—", "read", "—", "—"],
  ["Revisions ✱ new",         "—", "✓", "—", "—", "✓", "—", "Client Portal request/approve"],
  ["Generate Invoice",        "✓", "—", "—", "✓", "—", "—", "—"],
  ["Record Payment",          "✓", "—", "—", "✓", "—", "—", "—"],
  ["Manage Employees",        "✓", "read", "✓", "—", "—", "—", "—"],
  ["Asset Lifecycle ✱ v2",    "✓", "assign", "✓", "PO/dispose", "—", "—", "own (via Emp Portal)"],
  ["Audit Logs",              "✓", "read", "read", "read", "—", "—", "—"],
];
const ROLES_H = ["MD", "Ops Mgr", "HR", "Accounts", "Team Lead", "Designer", "Portal"];

export default function Roadmap() {
  const { data: org } = useQuery({ queryKey: ["org"], queryFn: async () => (await api.get("/meta/organization")).data });

  return (
    <div data-testid="roadmap-page">
      <PageHeader
        eyebrow="Consulting Package · Architecture v3 · Foundation v1.1 · APPROVED"
        title="Architecture & Roadmap"
        description="Foundation v1.1 locked. 8 permanent design principles + 5 frameworks (Rules, Standards, Dashboards, Configuration, Development) baked in. Sprint 2 delivered; Sprint 3 next up."
      />
      <div className="max-w-[1400px] mx-auto px-8 py-6">
        <div className="mb-6 border border-emerald-300 bg-emerald-50/60 rounded-lg p-4 flex items-start gap-3" data-testid="v2-banner">
          <div className="w-8 h-8 rounded-md bg-emerald-500 text-white grid place-items-center shrink-0">
            <CheckCircle2 className="w-4 h-4" />
          </div>
          <div className="text-sm text-emerald-900 leading-relaxed">
            <div className="font-heading font-bold">Sprint 2 · delivered</div>
            The Business Division loop (Estimation → Quotation → Public Approval → Handover → Project → Invoice → Payment) is fully live. All 12 architectural principles are honoured. See the ADR log in <code className="font-mono text-xs bg-white/60 rounded px-1">/app/memory/06_technical_decisions.md</code>.
          </div>
        </div>

        <Tabs defaultValue="workflow">
          <TabsList data-testid="rm-tabs">
            <TabsTrigger value="workflow" data-testid="rm-tab-workflow">Business Workflow v2</TabsTrigger>
            <TabsTrigger value="architecture" data-testid="rm-tab-arch">Architecture</TabsTrigger>
            <TabsTrigger value="principles" data-testid="rm-tab-principles">12 Principles</TabsTrigger>
            <TabsTrigger value="foundation" data-testid="rm-tab-foundation">Foundation v1.1</TabsTrigger>
            <TabsTrigger value="roles" data-testid="rm-tab-roles">Roles & Permissions v2</TabsTrigger>
            <TabsTrigger value="sprints" data-testid="rm-tab-sprints">Sprint Plan v2</TabsTrigger>
            <TabsTrigger value="changes" data-testid="rm-tab-changes">v1 → v2 Changes</TabsTrigger>
          </TabsList>

          <TabsContent value="workflow" className="mt-6">
            <div className="border border-zinc-200 rounded-lg bg-white p-8" data-testid="workflow-panel">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">The End-to-End Business Lifecycle · Client-Relationship-Centric</div>
              <h2 className="font-heading text-2xl font-bold mt-1 mb-6">20 stages · 3 Divisions · 1 client relationship</h2>
              <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {WORKFLOW.map((w, i) => (
                  <li key={w.s} className={`border rounded-md p-3 flex items-start gap-3 ${w.new ? "border-emerald-300 bg-emerald-50/50" : "border-zinc-200 bg-zinc-50"}`}>
                    <div className="font-mono text-xs text-zinc-500 shrink-0 w-6">{String(i + 1).padStart(2, "0")}</div>
                    <div>
                      <div className="text-sm font-medium">{w.s}</div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">{w.d}</div>
                      {w.new && <div className="mt-1"><span className="text-[9px] font-semibold uppercase tracking-widest text-emerald-700 bg-emerald-100 rounded px-1.5 py-0.5">v2 new</span></div>}
                    </div>
                  </li>
                ))}
              </ol>
              <p className="mt-8 text-sm text-zinc-600 max-w-3xl leading-relaxed">
                Stages 01–07 belong to the <span className="font-medium">Business Division</span>. Stage 08 <span className="font-medium">Project Handover</span> is the formal transition between divisions. Stages 09–17 belong to <span className="font-medium">Operations</span>. Stages 18–20 return to <span className="font-medium">Business</span>. Every transition writes to <code className="font-mono text-xs">audit_logs</code>.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="architecture" className="mt-6 space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div className="border border-zinc-200 rounded-lg bg-white p-6">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Frontend</div>
                <div className="font-heading text-lg font-bold mt-1">React 19 · TanStack · Shadcn</div>
                <p className="text-xs text-zinc-500 mt-2">Manrope + Geist · Tailwind · Recharts · Sonner</p>
              </div>
              <div className="border border-zinc-200 rounded-lg bg-white p-6">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Backend</div>
                <div className="font-heading text-lg font-bold mt-1">FastAPI · Motor · JWT</div>
                <p className="text-xs text-zinc-500 mt-2">bcrypt · Emergent Google Auth · JWT audience per portal</p>
              </div>
              <div className="border border-zinc-200 rounded-lg bg-white p-6">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Data</div>
                <div className="font-heading text-lg font-bold mt-1">MongoDB (async)</div>
                <p className="text-xs text-zinc-500 mt-2">Multi-tenant · custom UUIDs · timezone-aware ISO · audit-first</p>
              </div>
            </div>

            <div className="border border-zinc-200 rounded-lg bg-white p-8">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Multi-Tenancy Hierarchy</div>
              <pre className="mt-4 font-mono text-xs text-zinc-700 leading-relaxed whitespace-pre overflow-x-auto">
{`Organization   →   Dream Home Engineers Pvt Ltd
 └── Company    →   Legal entities (multi-currency ready)
      └── Branch  →   HQ Delhi · Mumbai · Bengaluru
           └── Department  →   Architecture · Structural · BIM · Interior
                └── User  →   role: MD | Ops Mgr | HR | Team Lead | Designer | Portal

Every collection carries organization_id — enforced by require_role + query scope.
JWT audience: erp | client_portal | employee_portal   (Sprint 6)`}
              </pre>
              {org && (
                <div className="mt-6 border border-dashed border-zinc-300 rounded-md p-4 text-sm">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Live tenant</div>
                  <div className="font-heading font-bold mt-1">{org.legal_name}</div>
                  <div className="font-mono text-xs text-zinc-500">GSTIN {org.gstin} · {org.country} · {org.currency} · {org.timezone}</div>
                </div>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="border border-zinc-200 rounded-lg bg-white p-6">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Client as Primary Entity (v2)</div>
                <pre className="mt-3 font-mono text-[11px] text-zinc-700 leading-relaxed">
{`Client
 ├── ClientContact[]
 ├── RequirementAnalysis[]
 ├── Estimation[]         ← new
 ├── Quotation[]
 ├── Handover[]           ← new
 ├── Project[]
 │    ├── ResourcePlan    ← new
 │    ├── TeamAssignment
 │    ├── Task[]
 │    ├── ProductionHour[]
 │    ├── Revision[]      ← new (first-class)
 │    ├── QARecord[]
 │    └── Delivery[]
 ├── Invoice[]  → Payment[]
 ├── Communication[]
 └── Document[]           ← Sprint 5`}
                </pre>
              </div>
              <div className="border border-zinc-200 rounded-lg bg-white p-6">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Industry Metadata on Project (Sprint 4)</div>
                <pre className="mt-3 font-mono text-[11px] text-zinc-700 leading-relaxed">
{`Project.metadata {
  discipline: [Architectural, Structural, MEP, BIM,
               Interior, Landscape, Urban, Civil, RC]
  project_type: new_build | renovation | as_built | ...
  lod: 100 | 200 | 300 | 350 | 400 | 500
  drawing_types: [Concept, Working, GA, As-Built, ...]
  software: [Revit, AutoCAD, ArchiCAD, SketchUp, 3ds Max,
             Enscape, Lumion, Rhino, Navisworks, Tekla, ...]
  deliverable_formats: [DWG, RVT, IFC, PDF, SKP, 3DM, ...]
  reality_capture: {
    matterport_ids: [], point_clouds: [E57 | LAS | LAZ],
    drone: bool, laser_scanner: bool, three_sixty: bool
  }
  physical: { floor_area, plot_area, floors, units, value }
  location: { city, country, coords, timezone }
  compliance: [NBC, IBC, IS, ASHRAE, LEED, IGBC, WELL]
}`}
                </pre>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="roles" className="mt-6">
            <div className="border border-zinc-200 rounded-lg bg-white overflow-x-auto" data-testid="roles-table">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                    <th className="px-4 py-3">Capability</th>
                    {ROLES_H.map((r) => <th key={r} className="px-3 py-3 text-center whitespace-nowrap">{r}</th>)}
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {PERMS.map((row) => (
                    <tr key={row[0]}>
                      <td className="px-4 py-3 font-medium whitespace-nowrap">
                        {row[0].replace(/ ✱ new| ✱ v2/, "")}
                        {row[0].includes("✱ new") && <span className="ml-2 text-[9px] font-semibold uppercase tracking-widest text-emerald-700 bg-emerald-100 rounded px-1.5 py-0.5">v2 new</span>}
                        {row[0].includes("✱ v2") && <span className="ml-2 text-[9px] font-semibold uppercase tracking-widest text-amber-700 bg-amber-100 rounded px-1.5 py-0.5">v2 expanded</span>}
                      </td>
                      {row.slice(1).map((cell, i) => (
                        <td key={i} className="px-3 py-3 text-center text-xs">
                          {cell === "✓" && <span className="text-emerald-600 font-bold">✓</span>}
                          {cell === "read" && <span className="text-zinc-500 uppercase tracking-widest text-[10px]">read</span>}
                          {cell === "—" && <span className="text-zinc-300">—</span>}
                          {!["✓", "read", "—"].includes(cell) && <span className="text-[10px] text-zinc-700">{cell}</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="sprints" className="mt-6">
            <div className="grid md:grid-cols-2 gap-4" data-testid="sprints-grid">
              {SPRINTS.map((s) => (
                <div
                  key={s.id}
                  className={`border rounded-lg p-6 ${s.status === "live" ? "border-emerald-300 bg-emerald-50/40" : s.status === "next" ? "border-amber-300 bg-amber-50/30" : "border-zinc-200 bg-white"}`}
                  data-testid={`sprint-${s.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-mono text-xs text-zinc-500">Sprint {s.id.slice(1)}</div>
                    {s.status === "live" && <span className="text-[10px] font-semibold uppercase tracking-widest text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Live</span>}
                    {s.status === "next" && <span className="text-[10px] font-semibold uppercase tracking-widest text-amber-700 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Next · pending approval</span>}
                    {s.id === "S7" && <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI</span>}
                  </div>
                  <div className="font-heading text-lg font-bold mt-1">{s.title}</div>
                  <ul className="mt-4 space-y-1.5 text-sm text-zinc-700">
                    {s.items.map((it) => <li key={it} className="flex items-start gap-2"><span className="w-1 h-1 rounded-full bg-zinc-400 mt-2 shrink-0" /> {it}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="principles" className="mt-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="principles-grid">
              {[
                ["01", "Platform First", "Dream Home ERP is a Business Operating System, not a project tracker. Every decision optimizes for the multi-company future."],
                ["02", "Multi-Tenant First", "Organization → Company → Business Unit → Department → Branch → Team is permanent from day one."],
                ["03", "Client at the Core", "Every project, quotation, invoice, payment, communication, document rolls up to a Client 360°."],
                ["04", "Estimation is a Core Asset", "Hours · Cost · Resources · Risk · Assumptions · Software · Complexity · Discipline · LOD · versioned history."],
                ["05", "Handover is an Official Event", "MD → Ops audited transition. On acceptance, Project is created and ownership crosses divisions."],
                ["06", "Production is the Heart of Ops", "Daily/weekly/monthly · billable/non-billable · productivity · team & client utilization."],
                ["07", "Revenue ↔ Operations", "Approved Production → Invoice Draft → Approval → Sent → Payment → Reports. No duplicate entry."],
                ["08", "Industry-Specific", "Architecture · Structural · BIM · CAD · Point Cloud · Matterport · Interior. Never generic ERP."],
                ["09", "Configurable Platform", "Departments · Project Types · Drawing Types · Disciplines · Workflows · Statuses · Templates — no code."],
                ["10", "AI Ready", "No AI in V1. Schema & routes future-proof so AI plugs in without touching business modules."],
                ["11", "Documentation First", "Business Arch · ER · Permissions · Roadmap · Workflow · Technical Decisions — living documents."],
                ["12", "Enterprise Design Standard", "Microsoft 365 · Zoho · Odoo · Atlassian · Notion · Linear · Vercel. Never legacy-ERP aesthetics."],
              ].map(([n, t, d]) => (
                <div key={n} className="border border-zinc-200 rounded-lg bg-white p-5" data-testid={`principle-${n}`}>
                  <div className="font-mono text-xs text-zinc-400">Principle {n}</div>
                  <div className="font-heading text-lg font-bold mt-1">{t}</div>
                  <div className="text-sm text-zinc-600 mt-2 leading-relaxed">{d}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 text-xs text-zinc-500 font-mono">
              Full text: <code>/app/memory/05_architecture_principles.md</code> · ADR log: <code>/app/memory/06_technical_decisions.md</code>
            </div>
          </TabsContent>

          <TabsContent value="foundation" className="mt-6">
            <div className="grid md:grid-cols-2 gap-4" data-testid="foundation-grid">
              <div className="border border-emerald-300 bg-emerald-50/40 rounded-lg p-6 md:col-span-2">
                <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Platform Foundation · v1.1 · APPROVED</div>
                <div className="font-heading text-2xl font-extrabold mt-1">8 Permanent Product Principles + 5 Frameworks</div>
                <p className="mt-2 text-sm text-zinc-700 leading-relaxed">Every future module honours these. Full docs live in <code className="font-mono text-xs bg-white/60 rounded px-1">/app/memory/09..15</code>. Master index at <code className="font-mono text-xs bg-white/60 rounded px-1">15_platform_foundation_v1_1.md</code>.</p>
              </div>
              {[
                ["1", "One Source of Truth", "Every business fact lives in exactly one place. Reports derive from truth, never copy it."],
                ["2", "Client-Centric Architecture", "Every entity declares client_id. Client 360° is the reference."],
                ["3", "Business & Operations Separation", "Business owns pricing & revenue. Operations owns delivery. Handover is the audited boundary."],
                ["4", "Production Drives Revenue", "Approved hours → Invoice draft. Money follows work, never the other way around."],
                ["5", "Configuration Before Customization", "Statuses, disciplines, drawing types, templates live in platform_config — not code."],
                ["6", "AI Assists, Humans Approve", "AI drafts; humans send. No autonomous mutations. Every AI artefact stamped."],
                ["7", "Multi-Company First", "Organization → Company → Business Unit → Department → Branch → Team from row 1."],
                ["8", "Security by Design", "Audit-first · RBAC · httpOnly cookies · single-use public tokens · bcrypt · CORS explicit."],
              ].map(([n, t, d]) => (
                <div key={n} className="border border-zinc-200 rounded-lg bg-white p-5" data-testid={`foundation-principle-${n}`}>
                  <div className="font-mono text-xs text-zinc-400">Principle {n}</div>
                  <div className="font-heading text-lg font-bold mt-1">{t}</div>
                  <div className="text-sm text-zinc-600 mt-2 leading-relaxed">{d}</div>
                </div>
              ))}
              <div className="border border-zinc-200 rounded-lg bg-white p-5 md:col-span-2" data-testid="foundation-frameworks">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">The 5 Frameworks</div>
                <ul className="mt-3 grid md:grid-cols-2 gap-3 text-sm">
                  <li className="border border-zinc-200 rounded p-3"><div className="font-heading font-bold">Business Rules</div><div className="text-zinc-600 text-xs mt-1">Approval / notification / status / validation / escalation rules configured by admins. Engine reserved for S5; bridging code today.</div></li>
                  <li className="border border-zinc-200 rounded p-3"><div className="font-heading font-bold">Platform Standards</div><div className="text-zinc-600 text-xs mt-1">DB · naming · statuses · forms · tables · documents · notifications · audit · attachments. Module checklist per PR.</div></li>
                  <li className="border border-zinc-200 rounded p-3"><div className="font-heading font-bold">Dashboard Standards</div><div className="text-zinc-600 text-xs mt-1">Per-workspace philosophy: Business / Ops Command Center / Finance / HR / Admin / Executive Command Center / AI Studio.</div></li>
                  <li className="border border-zinc-200 rounded p-3"><div className="font-heading font-bold">Configuration</div><div className="text-zinc-600 text-xs mt-1">14 config keys locked: disciplines, project_types, drawing_types, software_stack, LOD, departments, notification_rules + templates + workflows + attachment_rules + working_calendar + currency_rates.</div></li>
                  <li className="border border-zinc-200 rounded p-3 md:col-span-2"><div className="font-heading font-bold">Development Standards</div><div className="text-zinc-600 text-xs mt-1">Sprint Definition of Done · module checklist · async-only backend · React Query frontend · data-testid on every element · audit-first mutations · never hard-code enums · never `npm` (always `yarn`).</div></li>
                </ul>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="changes" className="mt-6">
            <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden" data-testid="changes-table">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                    <th className="px-4 py-3">Refinement</th>
                    <th className="px-4 py-3">Placed in</th>
                    <th className="px-4 py-3">Rationale</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 text-[13px]">
                  {[
                    ["Clients elevated to primary navigation", "Sprint 2", "Reinforces the client-relationship-centric philosophy — Projects nested inside Client 360"],
                    ["Estimation module (between Requirement & Quotation)", "Sprint 2", "Separates internal price from client-facing quotation → tracks margin per project"],
                    ["Project Handover workflow", "Sprint 2", "Formalizes ownership transition Business → Operations with audit trail"],
                    ["Resource Planning + Workload Management", "Sprint 3", "Precedes Team Assignment; utilization visibility + over-allocation warnings"],
                    ["Revision Management (first-class · unlimited rounds)", "Sprint 3", "Multi-round revision cycles with billable/non-billable + task auto-generation"],
                    ["Industry Project Metadata", "Sprint 4", "Matterport · Point Cloud · Revit · ArchiCAD · AutoCAD · disciplines · LOD · drawing types"],
                    ["Asset Lifecycle (full lifecycle)", "Sprint 5", "Procurement → Provisioning → Utilization → Maintenance → Depreciation → Disposal"],
                    ["Client Portal (reserved)", "Sprint 6", "External tokenized portal — quotation approvals, revisions review, deliverables"],
                    ["Employee Portal (reserved)", "Sprint 6", "Internal self-service — hours, leaves, payslips, assets, docs"],
                  ].map(([r, s, why]) => (
                    <tr key={r}>
                      <td className="px-4 py-3 font-medium">{r}</td>
                      <td className="px-4 py-3 whitespace-nowrap"><span className="font-mono text-xs bg-zinc-100 rounded px-2 py-0.5">{s}</span></td>
                      <td className="px-4 py-3 text-zinc-600">{why}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
